import useDocumentTitle from "hooks/useDocumentTitle";
import { TabPanel, TabView } from "primereact/tabview";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { userSelector } from "store/slices/user";

import { BenefitComparisonTab } from "./BenefitComparisonTab";
import { LineTypesTab } from "./LineTypesTab";
import { PayerOverridesTab } from "./PayerOverridesTab";
import { RemitAccuracyTab } from "./RemitAccuracyTab";

/**
 * CLE-2792 — Eligibility: the payer-agnostic settings behind every eligibility check.
 *
 * A TABBED page. The tabs are not decoration: line types and payer overrides both belong to
 * eligibility as a whole rather than to one account or one payer, and giving the first one a page of
 * its own would have meant moving it when the second arrived — which it now has.
 *
 * Payer overrides come first and open by default (Mike, 2026-09-14): they are the tab edited day
 * to day — a payer reporting the wrong copay is a recurring event — while a line type definition
 * is written once per payer wording and then left alone. The engine reads them in the other order
 * (line selection first, then the override on top of it); the order the engine runs them is not
 * the order a person needs them.
 *
 * CLE-2820 — SUPER ADMIN, and nothing narrower. The page used to hide itself from every super admin
 * but two named addresses while the engine was mid-cutover, and returned `null` for everyone else —
 * a blank screen at a real route. The route is `WithRoleProtection allowedRoles={[SUPER_ADMIN]}` and
 * every endpoint these tabs call requires SuperAdmin, so the body simply renders: whoever got here
 * is allowed to be here, and the API is the boundary either way.
 *
 * The one exception is the fourth tab, which its owner is still iterating on and is showing to
 * nobody else until he is happy with it — `COMPARISON_TAB_PREVIEW_EMAILS` below, which is temporary
 * and covers that tab alone. The page and the other three tabs are the whole SuperAdmin role.
 */
/** The `?tab=` value of each tab, in tab order. An unknown or missing value opens the first. */
export const ELIGIBILITY_TABS = [
  "payer-overrides",
  "line-types",
  "remit-accuracy",
  "legacy-engine-ai",
] as const;

/**
 * 🚨 TEMPORARY, ONE TAB, ONE PERSON — DELETE THIS CONSTANT TO OPEN THE TAB TO EVERY SUPER ADMIN.
 *
 * Mike asked for this on 2026-09-17: "Legacy vs Engine vs AI" shipped a few hours earlier and he is
 * still iterating on it — the tiles, the wording and the cluster are all still moving — so he wants
 * it in front of nobody else until he is happy with it. A half-finished screen read as a finished
 * one is the cost being avoided; there is nothing sensitive behind it.
 *
 * It is NOT the `useBenefitEngineAdmin` hook this ticket deleted earlier the same day, and must not
 * grow back into one: that hook hid the whole Eligibility page, the hospital-edit engine card and
 * the AI label from every super admin but two named addresses, and left a blank screen at a real
 * route. This covers ONE tab of four. The other three tabs, the page itself, the engine card and
 * the AI label are open to every super admin and stay that way. Nothing else may read this list —
 * if a second screen ever wants gating, that is a permission, not another email.
 *
 * NOT ACCESS CONTROL. `GET /benefitComparison/*` is SuperAdmin on every route and is unchanged, so
 * any super admin can still call it; this only decides whether an unfinished tab is on screen.
 */
const COMPARISON_TAB_PREVIEW_EMAILS = ["mike.over@clearhealthinc.com"];

export function EligibilityPage() {
  useDocumentTitle("Eligibility");
  const [params, setParams] = useSearchParams();
  const { email } = useSelector(userSelector);

  const showComparisonTab = COMPARISON_TAB_PREVIEW_EMAILS.includes(
    (email ?? "").trim().toLowerCase()
  );

  // The tabs this viewer actually has, in tab order — the index the TabView counts in. A `?tab=` naming
  // a tab they cannot see is unknown to them and opens the first, exactly like any other unknown value.
  const visibleTabs = showComparisonTab
    ? ELIGIBILITY_TABS
    : ELIGIBILITY_TABS.filter((tab) => tab !== "legacy-engine-ai");

  const activeIndex = Math.max(
    0,
    visibleTabs.indexOf(params.get("tab") as (typeof ELIGIBILITY_TABS)[number])
  );

  // A tab change PUSHES a history entry, so Back returns to the previous tab. The other tabs' own params
  // (the remit accuracy filters) are dropped: they belong to the tab being left.
  const changeTab = (index: number) => setParams({ tab: visibleTabs[index] });

  return (
    <div className="p-4" data-testid="eligibilityPage">
      <h2 className="m-0 mb-3">Eligibility</h2>
      <TabView
        data-testid="eligibilityPage.tabs"
        activeIndex={activeIndex}
        onTabChange={(event) => changeTab(event.index)}
      >
        <TabPanel header="Payer overrides">
          <PayerOverridesTab />
        </TabPanel>
        <TabPanel header="Line types">
          <LineTypesTab />
        </TabPanel>
        {/* CLE-2818 — internal measurement of the engine against 835 remits. */}
        <TabPanel header="Remit accuracy">
          <RemitAccuracyTab />
        </TabPanel>
        {/*
          CLE-2820 PR 3 — the three pickers against each other on every check, remit or not.
          Last, and hidden from everyone but its owner while he iterates on it (see
          COMPARISON_TAB_PREVIEW_EMAILS). A `false` child is skipped by TabView — no header, no
          panel, no gap — and the tabs before it keep their indices.
        */}
        {showComparisonTab && (
          <TabPanel header="Legacy vs Engine vs AI">
            <BenefitComparisonTab />
          </TabPanel>
        )}
      </TabView>
    </div>
  );
}
